import { Component } from 'react';
import debounce from 'lodash/debounce';
import omit from 'lodash/omit';

import withStyles from '@mui/styles/withStyles';
import AutocompleteSuggestions from '../AutocompleteSuggestions';

const styles = ({ spacing, typography }) => ({
  caption: {
    margin: `${spacing(1)} 0`,
    ...typography.caption,
  },
  hidden: {
    display: 'none',
  },
});

export class Autocomplete extends Component {
  static defaultProps = {
    caseSensitive: false,
    caption: '',
    classes: {},
    className: '',
    debounce: 200,
    defaultValue: {},
    excludedIds: [],
    minCharacters: 2,
    resetOnSelect: false,
    shouldHideInput: false,
    suggestionRenderer: null,
    onClear() {},
  };

  constructor(props) {
    super(props);

    this.state = {
      suggestions: [],
      inputValue: props.defaultValue.value,
      hasFocus: false,
    };

    this.controlsId = `i-${Math.random().toString(16)}`;

    this.input = null;

    this.suggestValuesDebounced = debounce(this.suggestValues, props.debounce).bind(this);
    this.onSelectSuggestionHandler = this.onSelectSuggestionHandler.bind(this);
  }

  componentDidMount() {
    const { defaultValue } = this.props;

    if (defaultValue) {
      this.input.value = defaultValue.value || '';
    }
  }

  onSelectSuggestionHandler(suggestion) {
    const { resetOnSelect, onSelectValue } = this.props;

    this.setState(
      () => {
        this.input.value = resetOnSelect ? '' : suggestion.value;

        return {
          suggestions: [],
          inputValue: this.input.value,
        };
      },
      () => {
        onSelectValue(suggestion);
      }
    );
  }

  async getDataSource() {
    const { dataSource } = this.props;

    const dataSourceType = typeof dataSource;
    const isFunction = dataSourceType === 'function';

    return isFunction ? dataSource(this.input.value) : dataSource;
  }

  inputBlurHandler = () => {
    this.setState(() => ({ hasFocus: false }));
  };

  inputFocusHandler = () => {
    this.setState(() => ({ hasFocus: true }));
  };

  doesDataMatchValue(data = {}, inputValue, strictEqual = false) {
    const { caseSensitive } = this.props;
    let dataValue = data.value;
    let valueToCompare = inputValue;

    if (!dataValue || !valueToCompare) {
      return false;
    }

    if (!caseSensitive) {
      dataValue = dataValue.toLowerCase();
      valueToCompare = valueToCompare.toLowerCase();
    }

    return strictEqual ? dataValue === valueToCompare : dataValue.indexOf(valueToCompare) >= 0;
  }

  suggestValues() {
    const { excludedIds, minCharacters, onClear } = this.props;
    const { inputValue } = this.state;

    const inputLength = this.input.value.length;
    const isTooShort = inputLength < minCharacters;

    const isSameValue = inputValue === this.input.value;

    if (!isSameValue && !inputLength) {
      onClear();
    }

    if (isSameValue || isTooShort) {
      this.setState((prevState) => ({
        suggestions: isTooShort ? [] : prevState.suggestions,
        inputValue: this.input.value,
      }));

      return Promise.resolve([]);
    }

    return this.getDataSource().then((allDatas = []) => {
      const suggestions = allDatas
        .filter((data) => this.doesDataMatchValue(data, this.input.value))
        .filter((data) => excludedIds.indexOf(data.id) === -1);

      this.setState(() => ({
        suggestions,
        inputValue: this.input.value,
      }));

      return suggestions;
    });
  }

  render() {
    const {
      caption,
      classes,
      className = '',
      shouldHideInput,
      suggestionRenderer,
      ...otherProps
    } = this.props;

    const { hasFocus, suggestions } = this.state;

    const omittedProps = [
      'caseSensitive',
      'dataSource',
      'debounce',
      'onClear',
      'onSelectValue',
      'minCharacters',
      'resetOnSelect',
      'excludedIds',
    ];

    const hiddenInputClassName = shouldHideInput ? classes.hidden : '';

    return (
      <span className="autocomplete">
        <input
          className={`autocomplete__input ${className} ${hiddenInputClassName}`}
          onKeyDown={this.suggestValuesDebounced}
          onKeyUp={this.suggestValuesDebounced}
          onChange={this.suggestValuesDebounced}
          onBlur={this.inputBlurHandler}
          onFocus={this.inputFocusHandler}
          role="combobox"
          aria-autocomplete="list"
          aria-controls={this.controlsId}
          aria-expanded={!!suggestions.length}
          autoComplete="off"
          ref={(ref) => {
            this.input = ref;
          }}
          /* eslint-disable-next-line react/jsx-props-no-spreading */
          {...omit(otherProps, omittedProps)}
        />
        {!suggestions.length && caption && (
          <p
            className={`autocomplete__caption ${classes.caption}`}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: caption }}
          />
        )}
        {hasFocus && (
          <AutocompleteSuggestions
            id={this.controlsId}
            suggestions={suggestions}
            onSelect={this.onSelectSuggestionHandler}
            suggestionRenderer={suggestionRenderer}
          />
        )}
      </span>
    );
  }
}

export default withStyles(styles)(Autocomplete);
